import { v1Delete, v1Get, v1Post } from '@/services/v1Api';

/**
 * Symphony Bot Service
 * Handles communication with the AI backend for Symphony Bot chat functionality
 */
export class AIChatService {
    /**
     * Send a message to the AI backend
     * @param {string} message - The user's message
     * @param {Object} context - Additional context (user info, current page, etc.)
     * @returns {Promise<Object>} AI response
     */
    static async sendMessage(message, context = {}) {
        try {
            const { data, meta } = await v1Post('/api/v1/ai/chat', {
                message: message,
                context: {
                    user_id: context.userId,
                    current_page: context.currentPage,
                    user_role: context.userRole,
                    timestamp: new Date().toISOString(),
                    ...context
                }
            });
            const payload = typeof data === 'object' && data ? data : {};
            return {
                success: true,
                message: payload.message ?? payload.reply ?? payload.content ?? data,
                ...payload,
                meta,
            };
        } catch (error) {
            console.error('Error sending message to AI:', error);
            throw error;
        }
    }

    /**
     * Get chat history for a user
     * @param {string} userId - User ID
     * @param {number} limit - Number of messages to retrieve
     * @returns {Promise<Array>} Chat history
     */
    static async getChatHistory(userId, limit = 50) {
        try {
            const { data } = await v1Get(`/api/v1/ai/chat/history/${userId}`, { limit });
            return Array.isArray(data) ? data : (data?.messages ?? data ?? []);
        } catch (error) {
            console.error('Error fetching chat history:', error);
            throw error;
        }
    }

    /**
     * Clear chat history for a user
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Confirmation response
     */
    static async clearChatHistory(userId) {
        try {
            await v1Delete(`/api/v1/ai/chat/history/${userId}`);
            return { success: true };
        } catch (error) {
            console.error('Error clearing chat history:', error);
            throw error;
        }
    }

    /**
     * Get AI suggestions based on current context
     * @param {Object} context - Current context (page, user role, etc.)
     * @returns {Promise<Array>} AI suggestions
     */
    static async getSuggestions(context = {}) {
        try {
            const { data } = await v1Post('/api/v1/ai/suggestions', {
                context: {
                    current_page: context.currentPage,
                    user_role: context.userRole,
                    timestamp: new Date().toISOString(),
                    ...context
                }
            });
            return Array.isArray(data) ? data : (data?.suggestions ?? data ?? []);
        } catch (error) {
            console.error('Error fetching AI suggestions:', error);
            throw error;
        }
    }

    /**
     * Check if AI service is available
     * @returns {Promise<boolean>} Service availability
     */
    static async checkAvailability() {
        try {
            const { data } = await v1Get('/api/v1/ai/health');
            return data?.available === true;
        } catch (error) {
            console.error('AI service not available:', error);
            return false;
        }
    }

    /**
     * Get AI model information
     * @returns {Promise<Object>} Model information
     */
    static async getModelInfo() {
        try {
            const { data } = await v1Get('/api/v1/ai/model-info');
            return data;
        } catch (error) {
            console.error('Error fetching model info:', error);
            throw error;
        }
    }

    /**
     * Send feedback about AI response
     * @param {string} messageId - Message ID
     * @param {string} feedback - User feedback (positive/negative)
     * @param {string} comment - Optional comment
     * @returns {Promise<Object>} Confirmation response
     */
    static async sendFeedback(messageId, feedback, comment = '') {
        try {
            const { data } = await v1Post('/api/v1/ai/feedback', {
                message_id: messageId,
                feedback: feedback,
                comment: comment,
                timestamp: new Date().toISOString()
            });
            return { success: true, ...(typeof data === 'object' && data ? data : { data }) };
        } catch (error) {
            console.error('Error sending feedback:', error);
            throw error;
        }
    }
}

export default AIChatService;
